import { canIn, type PermissionScope } from '@/lib/permissions/resolve'
import type { PermissionKey } from '@/lib/permissions/keys'
import { useWorkspaceStore } from '@/stores/workspace'

/**
 * UI-only permission check, resolved from the permissions the backend returns for each
 * organization/workspace. Controls visibility of navigation/actions — it is NOT a security
 * boundary: the backend independently authorizes every protected operation.
 *
 * Defaults to the currently selected workspace; pass a scope to check elsewhere
 * (e.g. `can(Permission.OrganizationCreate, 'anywhere')`). False until permissions have loaded.
 */
export function can(permission: PermissionKey, scope: PermissionScope = 'current'): boolean {
  const workspace = useWorkspaceStore()

  return (
    workspace.isLoaded &&
    canIn(
      {
        organizations: workspace.organizations,
        workspaces: workspace.accounts,
        currentOrganizationId: workspace.currentOrganizationId,
        currentWorkspaceId: workspace.currentAccountId,
      },
      permission,
      scope,
    )
  )
}

export function canAny(permissions: readonly PermissionKey[], scope?: PermissionScope): boolean {
  return permissions.some((permission) => can(permission, scope))
}

export function canAll(permissions: readonly PermissionKey[], scope?: PermissionScope): boolean {
  return permissions.every((permission) => can(permission, scope))
}
